import React, { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";
import { NativePickerField, addDays, dateKey, dayMonth, fromKey, rangeLabel, startOfDay, useOutsideClick } from "./dailyScheduleShared";

// Date range popover for the admin Daily Schedule: quick ranges (the default two weeks from today,
// the previous month) or any From/To dates up to MAX_RANGE_DAYS long.

export const DEFAULT_RANGE_DAYS = 14;
export const MAX_RANGE_DAYS = 62;

export const daysBetween = (a, b) => Math.round((startOfDay(b) - startOfDay(a)) / 86400000);

// The same date one month earlier (31 Mar -> 28/29 Feb).
const monthBefore = (d) => {
  const r = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  r.setDate(Math.min(d.getDate(), new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate()));
  return r;
};

const rangePresets = (today) => [
  { id: "next-2-weeks", label: "Next 2 weeks", start: today, end: addDays(today, DEFAULT_RANGE_DAYS - 1) },
  { id: "previous-month", label: "Previous 1 month", start: monthBefore(today), end: addDays(today, -1) },
];

const keyLabel = (key) => `${dayMonth(fromKey(key))} ${fromKey(key).getFullYear()}`;

export default function DateRangePicker({ start, end, anchorRef, onApply, onClose }) {
  const [from, setFrom] = useState(dateKey(start));
  const [to, setTo] = useState(dateKey(end));
  const [error, setError] = useState("");
  const presets = rangePresets(startOfDay(new Date()));
  useOutsideClick(anchorRef, onClose, true);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isShown = (p) => dateKey(p.start) === dateKey(start) && dateKey(p.end) === dateKey(end);
  const apply = (e) => {
    e.preventDefault();
    if (!from || !to) return setError("Choose both dates");
    const a = fromKey(from);
    const b = fromKey(to);
    if (b < a) return setError("The To date must be on or after the From date");
    if (daysBetween(a, b) + 1 > MAX_RANGE_DAYS) return setError(`Choose a range of up to ${MAX_RANGE_DAYS} days`);
    return onApply(a, b);
  };
  const field = (setter) => (value) => { setter(value); setError(""); };

  return (
    <form className="ds-range-pop" role="dialog" aria-label="Choose dates" onSubmit={apply}>
      <p className="ds-range-pop__title">Show schedule for</p>
      <div className="ds-range-pop__presets">
        {presets.map((p) => (
          <button key={p.id} type="button" className={isShown(p) ? "is-active" : ""} aria-pressed={isShown(p)} onClick={() => onApply(p.start, p.end)}>
            <b>{p.label}</b>
            <span>{rangeLabel(p.start, p.end)}</span>
          </button>
        ))}
      </div>
      <p className="ds-range-pop__title">Custom range</p>
      <div className="ds-range-pop__fields">
        <div className="ds-field">
          <span>From</span>
          <NativePickerField type="date" label="From date" icon={<CalendarDays size={14} />} value={from} display={from ? keyLabel(from) : "Select date"} onChange={field(setFrom)} />
        </div>
        <div className="ds-field">
          <span>To</span>
          <NativePickerField type="date" label="To date" icon={<CalendarDays size={14} />} value={to} display={to ? keyLabel(to) : "Select date"} onChange={field(setTo)} />
        </div>
      </div>
      {error && <p className="ds-range-pop__error" role="alert">{error}</p>}
      <div className="ds-range-pop__foot">
        <button type="button" className="ds-btn ds-btn--ghost" onClick={onClose}>Cancel</button>
        <button type="submit" className="ds-btn ds-btn--primary">Apply</button>
      </div>
    </form>
  );
}
